import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Space, Button } from "react_reusable_components";
import {
  AUDIT_STATUSES,
  QUESTION_TYPE_CONSTANTS,
  userRole,
} from "../../constants";
import useResponsive from "../../core/hooks/useResponsive";
import { TwoColumn, TwoRow } from "../../core/layouts";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { setAuditQuestionnaireDetailById } from "../../globalContext/ManageAudit/auditActions";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import {
  getQuestionnaireById,
  getQuestionnaireList as getQuestionnaireListAction,
} from "../../globalContext/questionnaire/questionnaireActions";
import { QuestionnaireContext } from "../../globalContext/questionnaire/questionnaireProvider";
import AuditService from "../../services/audit-service";
import QuestionnaireService from "../../services/questionnaire-service";
import variables from "../../themes/base/styles/variables";
import { showNotification } from "../../UtilityFunctions/notification";
import { getIsAudit, getLocalizedMessage } from "../../UtilityFunctions/utils";
import AddQuestionnaireRightContainer from "../AddQuestionnaireRightContainer";
import QuestionnaireNavigationContainer from "../QuestionnaireNavigationContainer";
import Style from "./AddQuestionnaireMainContainerStyle.module.scss";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";

const AddQuestionnaireMainContainerStyle = {
  rightSection: {
    flex: "1 1 auto",
    height: variables.fullHeight,
    overflow: "unset",
  },
  mainTwoRowStyle: {
    height: variables.fullHeight,
  },
  mainTopSectionStyle: {
    paddingBottom: variables.paddingSmall,
    flex: 1,
  },
  twoColumnMainStyle: {
    gap: variables.gapSmall,
    height: variables.fullHeight,
    overflow: "unset",
  },
  bottomButtonSectionStyle: {
    paddingTop: variables.paddingSmall,
    justifyContent: "flex-end",
    marginRight: variables.marginMedium,
    borderTop: `1px solid ${variables.grey4}`,
  },
};
const AddQuestionnaireMainContainer = ({ mode }) => {
  const intl = useIntl();
  const [questionnaireState, questionnaireDispatch] =
    useContext(QuestionnaireContext);
  const [disabled, setDisabled] = useState(false);

  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [authState] = useContext(AuthContext);
  const [auditState, auditDispatch] = useContext(AuditContext);

  const navigate = useNavigate();
  const responsive = useResponsive();
  const { id, auditId } = useParams();

  const [questionnaireType, setQuestionnaireType] = useState(1);
  const [questionnaireName, setQuestionnaireName] = useState("");
  const [markAsPrimary, setMarkAsPrimary] = useState(false);
  const [questionsData, setQuestionsData] = useState([
    {
      sectionName: "",
      sequence: "1",
    },
  ]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [openNavigation, setOpenNavigation] = useState(responsive.isSm);
  const [selectedAuditInstance, setSelectedAuditInstance] = useState(0);
  const [reAuditDueDate, setReAuditDueDate] = useState(null);
  const [evidenceInProgress, setEvidenceInProgress] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const isQuestionnaire = window.location.href.includes("questionnaire");
  const isAudit = getIsAudit();
  const isAuditPreview = window.location.href.includes("audit-preview");
  const isDnsd = window.location.href.includes("dnsd");
  const isAuditBeingEdited = window.location.href.includes("edit-audit");

  useEffect(() => {
    if (isQuestionnaire && authState?.currentRole?.id !== userRole.CA) {
      QuestionnaireService.getPrimaryQuestionnaire({
        questionnaireDispatch,
        loaderDispatch,
        params: { condition: "primary" },
        intl,
      });
    }
    if (id || auditId) {
      if (isQuestionnaire) {
        QuestionnaireService.getQuestionnaireById({
          questionnaireDispatch,
          loaderDispatch,
          questionnaireCode: id,
          intl,
        });
      } else if (isAudit) {
        AuditService.getAuditQuestionnaireById({
          auditDispatch,
          loaderDispatch,
          auditId: id || auditId,
          intl,
        });
      } else if (isAuditPreview) {
        AuditService.getAuditPreviewById({
          auditDispatch,
          loaderDispatch,
          auditId: id,
          intl,
        });
      }
    }
    return () => {
      questionnaireDispatch(getQuestionnaireListAction(null));
      questionnaireDispatch(getQuestionnaireById({}));
      if (!isDnsd) {
        auditDispatch(setAuditQuestionnaireDetailById(null));
      }
      setQuestionsData([
        {
          sectionName: "",
          sequence: "1",
        },
      ]);
    };
  }, []);

  useEffect(() => {
    if (isQuestionnaire) {
      if (questionnaireState?.questionnaireData?.questionnaireName)
        setQuestionnaireName(
          questionnaireState?.questionnaireData?.questionnaireName
        );
      if (questionnaireState?.questionnaireData?.isRepair) {
        setQuestionnaireType(2);
      }
      if (questionnaireState?.questionnaireData?.isPrimary) {
        setMarkAsPrimary(true);
      }
      if (questionnaireState?.questionnaireData?.sections)
        setQuestionsData(questionnaireState?.questionnaireData?.sections);
    }
  }, [questionnaireState]);

  useEffect(() => {
    let auditData;
    if ((isAudit || isAuditPreview) && auditState?.auditDetail) {
      auditData = isAudit
        ? auditState?.auditDetail
        : auditState?.auditDetail?.[selectedAuditInstance];
    }
    if (auditData) {
      setQuestionnaireName(auditData?.questionnaireName);
      setQuestionsData(auditData?.sections);
      setReAuditDueDate(
        (isAuditPreview &&
          auditData?.currentStatus !== AUDIT_STATUSES.COMPLETED) ||
          isAudit
          ? auditData?.reAuditDueDate
          : null
      );
    }
  }, [auditState, selectedAuditInstance]);

  function handleSetExpandedKeys(data) {
    for (const item of data) {
      if (item?.sections || item?.questions) {
        const section = item;
        if (section?.sequence) {
          setExpandedKeys((prev) => [...prev, section.sequence]);
        }
        if (section?.sections) handleSetExpandedKeys(section?.sections);
      }
    }
  }

  useEffect(() => {
    if (questionsData || questionnaireState?.isVirtualHeightNeeded) {
      handleSetExpandedKeys(questionsData);
    }
  }, [questionsData, questionnaireState?.isVirtualHeightNeeded]);

  function onExpand(expandedKeysData) {
    setExpandedKeys(expandedKeysData);
  }

  function handleExpand(key, filter, append) {
    const isExpanded = expandedKeys?.includes(key);
    let newExpandedKeys;

    if (isExpanded && filter) {
      newExpandedKeys = expandedKeys?.filter(
        (expandedKey) => expandedKey !== key
      );
    } else if (append) {
      newExpandedKeys = [...expandedKeys, key];
    } else {
      newExpandedKeys = expandedKeys;
    }

    onExpand(newExpandedKeys);
  }

  function checkLowestLevelHasNoQuestion(questionsRecords) {
    for (const element of questionsRecords) {
      const section = element;
      if (section.sections && section.sections.length > 0) {
        if (checkLowestLevelHasNoQuestion(section.sections)) {
          return true;
        }
      } else if (!section.questions?.length) {
        return true;
      }
    }
    return false;
  }

  function checkQuestionDataMandatoryFieldsNotFilled(questionsRecords) {
    for (const element of questionsRecords) {
      const section = element;
      if (!section.sectionName || section.questions?.some((q) => !q.name)) {
        return true;
      }
      if (section.sections && section.sections.length > 0) {
        if (checkQuestionDataMandatoryFieldsNotFilled(section.sections)) {
          return true;
        }
      }
    }
    return false;
  }

  function checkQuestionsHaveOptionsFilled(quesData, checkAllFilled) {
    // NOSONAR
    for (const element of quesData) {
      const section = element;
      if (
        section.questions?.some((questionItem) => {
          const questionTypes = questionItem.questionType;
          const hasMultiOrSingleSelectType = questionTypes?.some(
            (typeObj) =>
              typeObj?.type === QUESTION_TYPE_CONSTANTS.SINGLE_SELECT ||
              typeObj?.type === QUESTION_TYPE_CONSTANTS.MULTI_SELECT
          );

          if (hasMultiOrSingleSelectType) {
            const invalidQuestion = questionTypes?.find((typeObj) => {
              if (
                typeObj?.type === QUESTION_TYPE_CONSTANTS.SINGLE_SELECT ||
                typeObj?.type === QUESTION_TYPE_CONSTANTS.MULTI_SELECT
              ) {
                if (checkAllFilled) {
                  return typeObj?.questionValue?.some((item) => !item.value);
                } else {
                  return (
                    !typeObj?.questionValue ||
                    typeObj?.questionValue?.length < 2 ||
                    !typeObj?.questionValue[0]?.value ||
                    !typeObj?.questionValue[1]?.value
                  );
                }
              }
              return false;
            });

            return Boolean(invalidQuestion);
          }

          return false;
        })
      ) {
        return true;
      }

      if (section.sections && section.sections.length > 0) {
        if (checkQuestionsHaveOptionsFilled(section.sections, checkAllFilled)) {
          return true;
        }
      }
    }
    return false;
  }

  function createPayloadSections(questionData) {
    // NOSONAR
    const payload = [];

    for (const element of questionData) {
      const question = element;
      const section = {
        sectionNumber: question.sequence,
        sectionName: question.sectionName,
      };

      if (question.questions && question.questions.length > 0) {
        section.questions = question.questions.map((q) => {
          const { questionType, id, ...rest } = q;

          const modifiedQuestionType = questionType?.map((typeObj) => {
            if (
              typeObj.type === QUESTION_TYPE_CONSTANTS.SINGLE_SELECT ||
              typeObj.type === QUESTION_TYPE_CONSTANTS.MULTI_SELECT
            ) {
              const questionValues = typeObj.questionValue?.map(
                (item, index) => ({
                  value: item.value,
                  sequence: index + 1,
                })
              );
              return {
                type: typeObj.type,
                ...(questionValues?.length && {
                  questionValue: questionValues?.filter((item) => item.value),
                }),
              };
            } else {
              return {
                type: typeObj.type,
              };
            }
          });

          return {
            ...rest,
            /** Uncomment this to revive question type functionality */

            // questionType:
            //   modifiedQuestionType[0]?.type ===
            //     QUESTION_TYPE_CONSTANTS.TEXTBOX &&
            //   modifiedQuestionType[1]?.type === QUESTION_TYPE_CONSTANTS.TEXTBOX
            //     ? [modifiedQuestionType[0]]
            //     : modifiedQuestionType,
          };
        });
      }

      if (question.sections && question.sections.length > 0) {
        section.sections = createPayloadSections(question.sections);
      }

      payload.push(section);
    }

    return payload;
  }

  const handleAddQuestionnaire = () => {
    let errorMessage = "";

    if (!questionnaireName) {
      errorMessage = "manageQuestionnaire.questionnaireNameRequired";
    } else if (checkQuestionDataMandatoryFieldsNotFilled(questionsData)) {
      {
        errorMessage = "manageQuestionnaire.requiredFields";
      }
    } else if (checkLowestLevelHasNoQuestion(questionsData)) {
      errorMessage = "manageQuestionnaire.lowestLevelHasNoQuestion";
    } else if (checkQuestionsHaveOptionsFilled(questionsData, false)) {
      errorMessage = "manageQuestionnaire.questionShouldHaveMinTwoOptions";
    } else if (checkQuestionsHaveOptionsFilled(questionsData, true)) {
      errorMessage = "manageQuestionnaire.questionShouldHaveAllOptionsFilled";
    }

    if (errorMessage) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, errorMessage)
      );
      return;
    }

    let payload = {
      questionnaireName: questionnaireName,
      sections: createPayloadSections(questionsData),
    };

    if (mode === "add")
      payload = {
        ...payload,
        isRepair: questionnaireType === 2,
        isOperational: questionnaireType === 1,
      };
    if (mode === "edit")
      payload = {
        ...payload,
        questionnaireDetailId:
          questionnaireState?.questionnaireData?.questionnaireDetailId,
        questionnaireCode:
          questionnaireState?.questionnaireData?.questionnaireCode,
        questionnaireId: questionnaireState?.questionnaireData?.questionnaireId,
      };

    if (authState?.currentRole?.id !== userRole.CA)
      payload = {
        ...payload,
        isPrimary: questionnaireType === 1 && markAsPrimary,
      };

    setDisabled(true);
    QuestionnaireService.addEditQuestionnaire(
      questionnaireDispatch,
      loaderDispatch,
      payload,
      navigate,
      intl,
      mode === "edit"
    )
      .then(() => {
        setDisabled(false);
      })
      .catch(() => {
        setDisabled(false);
      })
      .finally(() => {
        setDisabled(false);
      });
  };
  const hasValidResponse = (question) => {
    // const validResponse = question.questionType?.some(
    //   (questionTypeItems) =>
    //     questionTypeItems?.response?.length > 0 ||
    //     !isNaN(questionTypeItems?.response)
    // );
    // Uncomment if questionType is required

    let valid = false;
    if (authState?.currentRole?.id === userRole.TM) {
      valid = question?.questionResponse?.evaluationStatus;
    } else {
      valid = question?.questionResponse?.textResponse;
    }
    return valid;
  };

  const hasCompulsoryQuestionWithInvalidResponse = (sections, returnValue) => {
    for (const section of sections) {
      if (section.questions) {
        for (const question of section.questions) {
          if (
            (question?.isMandatory || returnValue) &&
            !hasValidResponse(question)
          ) {
            if (returnValue) return `${section.sequence}${question.sequence}`;
            else return true;
          }
        }
      }

      if (section.sections) {
        const hasInvalidResponse = hasCompulsoryQuestionWithInvalidResponse(
          section.sections,
          returnValue
        );
        if (hasInvalidResponse) {
          return hasInvalidResponse;
        }
      }
    }
    return false;
  };

  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    if (!hasRun && auditState?.auditDetail?.sections) {
      if (
        (auditState?.auditDetail?.auditStatus === AUDIT_STATUSES.IN_PROGRESS ||
          auditState?.auditDetail?.auditStatus === AUDIT_STATUSES.PENDING ||
          auditState?.auditDetail?.auditStatus ===
            AUDIT_STATUSES.RE_AUDIT_IN_PROGRESS) &&
        isAudit
      ) {
        const notFilledQuestionId = hasCompulsoryQuestionWithInvalidResponse(
          auditState?.auditDetail?.sections,
          true
        );
        if (notFilledQuestionId) {
          setTimeout(() => {
            document.getElementById(notFilledQuestionId)?.scrollIntoView();
          }, 1600);
        }
      }
      setHasRun(true);
    }
  }, [auditState?.auditDetail, hasRun]);

  const handleUpdateAuditStatus = () => {
    setDisabled(true);
    if (isAuditBeingEdited) {
      showNotification(
        "success",
        "Success",
        getLocalizedMessage(intl, "savedSuccessFully")
      );
      setDisabled(false);
      navigate("/audit-listing");
      return;
    }
    AuditService.getAuditQuestionnaireById({
      auditDispatch,
      loaderDispatch,
      auditId: id || auditId,
      intl,
    }).then((res) => {
      setDisabled(false);
      const auditPending = res?.data?.auditStatus === AUDIT_STATUSES.PENDING;
      if (auditPending) {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "manageAudit.fillAtLeastOneResponse")
        );
        return;
      }
      if (res?.data?.auditStatus === AUDIT_STATUSES.RE_AUDIT_PENDING) {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(
            intl,
            "manageAudit.fillAtLeastOneResponse.reAuditPending"
          )
        );
        return;
      }
      const notReadyToSubmit = hasCompulsoryQuestionWithInvalidResponse(
        res?.data?.sections,
        false
      );
      if (!notReadyToSubmit) {
        AuditService.updateAuditStatus({
          loaderDispatch,
          intl,
          body: { status: AUDIT_STATUSES.AUDIT_SUBMITTED },
          auditId: id || auditId,
          successMessageCode: "manageAudit.F2F.auditSubmittedSuccess",
          isDnsd,
        }).then(() => {
          if (isDnsd) {
            AuditService.getAuditQuestionnaireById({
              auditDispatch,
              loaderDispatch,
              auditId: id || auditId,
              intl,
            });
          } else {
            navigate("/audit-listing");
          }
          setDisabled(false);
        });
      } else {
        setDisabled(false);
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(
            intl,
            isDnsd
              ? "manageAudit.Self.mandatoryFieldsErrorForTM"
              : "manageAudit.F2F.mandatoryFieldsError"
          )
        );
      }
    });
  };

  useEffect(() => {
    if (
      isAudit &&
      auditState?.auditDetail?.auditStatus === AUDIT_STATUSES.PENDING
    ) {
      showNotification(
        "warning",
        "",
        getLocalizedMessage(intl, "manageAudit.autoSaveWarning")
      );
    }
  }, [isAudit, auditState?.auditDetail?.auditStatus]);

  return (
    <>
      {(auditState?.auditDetail || isQuestionnaire) && (
        <TwoRow
          style={AddQuestionnaireMainContainerStyle.mainTwoRowStyle}
          topSectionStyle={{
            ...AddQuestionnaireMainContainerStyle.mainTopSectionStyle,
            ...(mode === "view" && { paddingBottom: "0.5rem" }),
          }}
          topSection={
            <TwoColumn
              style={{
                ...AddQuestionnaireMainContainerStyle.twoColumnMainStyle,
                ...(!responsive.isSm && { gap: 0 }),
              }}
              leftSectionStyle={{ height: variables.fullHeight }}
              leftSection={
                <QuestionnaireNavigationContainer
                  questionsData={questionsData}
                  questionnaireName={questionnaireName}
                  expandedKeys={expandedKeys}
                  setExpandedKeys={setExpandedKeys}
                  mode={mode}
                  openNavigation={openNavigation}
                  setOpenNavigation={setOpenNavigation}
                  selectedAuditInstance={selectedAuditInstance}
                  setSelectedAuditInstance={setSelectedAuditInstance}
                  handleSetExpandedKeys={handleSetExpandedKeys}
                  absolute={!responsive.isSm}
                />
              }
              rightSection={
                <AddQuestionnaireRightContainer
                  questionsData={questionsData}
                  setQuestionsData={setQuestionsData}
                  questionnaireName={questionnaireName}
                  setQuestionnaireName={setQuestionnaireName}
                  reAuditDueDate={reAuditDueDate}
                  questionnaireType={questionnaireType}
                  setQuestionnaireType={setQuestionnaireType}
                  markAsPrimary={markAsPrimary}
                  setMarkAsPrimary={setMarkAsPrimary}
                  expandedKeys={expandedKeys}
                  setExpandedKeys={setExpandedKeys}
                  handleExpand={handleExpand}
                  onExpand={onExpand}
                  mode={isAudit || isAuditPreview ? "view" : mode}
                  evidenceInProgress={evidenceInProgress}
                  setEvidenceInProgress={setEvidenceInProgress}
                />
              }
              rightSectionStyle={
                AddQuestionnaireMainContainerStyle.rightSection
              }
            />
          }
          bottomSectionStyle={{
            ...(((mode !== "view" && isQuestionnaire) || isAudit) &&
              AddQuestionnaireMainContainerStyle.bottomButtonSectionStyle),
          }}
          bottomSection={
            ((mode !== "view" && isQuestionnaire) || isAudit) && (
              <Space size={0}>
                {isQuestionnaire && (
                  <Button
                    className={Style["cancel-btn"]}
                    ghost
                    icon={null}
                    onClick={() => navigate("/questionnaire")}
                  >
                    {getLocalizedMessage(intl, "button.text.cancel")}
                  </Button>
                )}
                <Button
                  disabled={evidenceInProgress || disabled}
                  className={Style["add-Questionnaire"]}
                  icon={null}
                  onClick={() => {
                    authState?.currentRole?.id === userRole.TM || isDnsd
                      ? setOpenModal(true)
                      : handleAddQuestionnaire();
                  }}
                >
                  {getLocalizedMessage(
                    intl,
                    authState?.currentRole?.id === userRole.TM || isDnsd
                      ? isAuditBeingEdited
                        ? "button.save"
                        : "button.submit"
                      : mode === "edit"
                      ? "manageQuestionnaire.editQuestionnaire.btn"
                      : "manageQuestionnaire.addQuestionnaire"
                  )}
                </Button>
              </Space>
            )
          }
        />
      )}

      <ConfirmModalContainer
        question={getLocalizedMessage(intl, "audit.submit.confirmation")}
        open={openModal}
        setOpen={setOpenModal}
        iconSrc={"/icons/warningIcon.svg"}
        onClose={() => {
          setOpenModal(false);
        }}
        onCancel={() => {
          setOpenModal(false);
        }}
        onConfirm={handleUpdateAuditStatus}
      />
    </>
  );
};

export default AddQuestionnaireMainContainer;
